<template>
  <div class="global-filter">
    <div class="global-filter__date-filter">
      <DateFilter
        v-model="stickyDateRange"
        @input="trackingStickyDateRange"
      />
    </div>

    <GlobalFilterMenu
      v-if="showGlobalFilterFeature && activeGlobalFilter !== null"
      :show-nonvisible-filter-counter="true"
    />

    <div class="global-filter__filter-actions">
      <button
        v-if="showClearAllButton"
        class="global-filter__clear-button"
        type="button"
        @click="clearAction"
      >
        {{ $t('components.GlobalFilter.label.button.clearButton') }}
      </button>

      <div
        v-if="showGlobalFilterFeature"
        class="global-filter__filter-button"
      >
        <DsButton
          icon
          color="secondary"
          name="filter"
          @click="openGlobalFilterModal"
        />
      </div>

      <div class="global-filter__modal">
        <GlobalFilterModal
          v-if="showGlobalFilterModal"
          @clear="clearAction"
          @cancel="cancelAction"
          @confirm="confirmAction"
        />
      </div>
    </div>
  </div>
</template>

<script>
import filtersModel from '@/mixins/filters-model';
import DateFilter from '@/components/DateFilter';
import { DsButton } from '@ds/components';
import GlobalFilterModal from '@/components/GlobalFilterModal';
import GlobalFilterMenu from '@/components/GlobalFilterMenu';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { Flags } from '@/plugins/rollout';
import { MUTATE } from '@/store/modules/mutation_types';
import moment from 'moment';
import { NORMALIZED_PATTERN } from '@/utils/date';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'GlobalFilter',
  components: {
    DateFilter,
    DsButton,
    GlobalFilterModal,
    GlobalFilterMenu,
  },
  mixins: [
    filtersModel,
  ],
  data() {
    return {
      showGlobalFilterModal: false,
      card: [],
      stickyDateRange: {},
      dateRangeChanged: false,
      ignoreDateRangeChanged: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeGlobalFilter',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapState('filter', [
      'dateRange',
      'globalFilter',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    showClearAllButton() {
      return Boolean(this.showGlobalFilterFeature && (Object.keys(this.globalFilter).length !== 0 || this.dateRangeChanged));
    },
  },
  watch: {
    stickyDateRange(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.mutateFilter({
        property: 'dateRange',
        with: {
          start: moment(newValue.start, NORMALIZED_PATTERN),
          end: moment(newValue.end, NORMALIZED_PATTERN),
          period: newValue.period,
        },
      });

      if (this.ignoreDateRangeChanged) {
        return;
      }

      this.dateRangeChanged = true;
      this.ignoreDateRangeChanged = false;
    },
  },
  async created() {
    this.mutateFilter({
      property: 'globalFilter',
      with: cloneDeep(this.activeGlobalFilter || {}),
    });

    const dateRangeURL = this.getDateRangeFromUrl();

    const startDate = dateRangeURL ? dateRangeURL.start : moment().subtract('6', 'day').startOf('day');
    const endDate = dateRangeURL ? dateRangeURL.end : moment().endOf('day');

    this.stickyDateRange = {
      start: startDate,
      end: endDate,
    };

    await this.$nextTick();
  },
  methods: {
    ...mapMutations('filter', {
      mutateFilter: MUTATE,
    }),
    ...mapActions('dashboard', [
      'saveGlobalFilter',
    ]),
    getDateRangeFromUrl() {
      const urlDateRange = this.$route.query.date_range;
      const dateRange = urlDateRange?.match(/[0-9]{12}:[0-9]{12}/u);

      if (dateRange) {
        const [start, end] = urlDateRange.split(':');
        return {
          start: moment(start, NORMALIZED_PATTERN),
          end: moment(end, NORMALIZED_PATTERN),
        };
      }
      return urlDateRange;
    },
    openGlobalFilterModal() {
      this.showGlobalFilterModal = true;

      trackEventWithUserProperties('ST GlobalFilter Modal Open', { Opened: this.showGlobalFilterModal });
    },
    closeGlobalFilterModal() {
      this.showGlobalFilterModal = false;
    },
    async clearAction() {
      this.stickyDateRange = {
        start: moment().subtract('6', 'day').startOf('day'),
        end: moment().endOf('day'),
      };

      this.mutateFilter({
        property: 'dateRange',
        with: this.stickyDateRange,
      });

      this.mutateFilter({
        property: 'globalFilter',
        with:
          {},
      });

      this.saveGlobalFilter({});

      await this.$nextTick();

      this.dateRangeChanged = false;

      trackEventWithUserProperties('ST GlobalFilter ClearButton Click');
    },
    cancelAction() {
      this.closeGlobalFilterModal();
    },
    confirmAction(hasChange) {
      this.dateRangeChanged = hasChange;

      this.stickyDateRange = this.dateRange;

      this.closeGlobalFilterModal();
    },
    trackingStickyDateRange(data) {
      trackEventWithUserProperties('ST Dashboard DateFilter Edit', { DateRange: data.period });
    },
  },
};

</script>

<style scoped lang="scss">
  .global-filter {
    background-color: var(--ds-color-neutral);
    width: 100%;
    height: 56px;
    display: flex;
    justify-content: flex-start;
    padding: 5px 0 12px 0;

    &__date-filter {
      padding-left: var(--ds-spacing-inline-sm);
      max-width: 234px;
    }

    &__filter-actions {
      display: flex;
      flex-direction: row;
      padding: 7px 0 0 48px;
      justify-content: flex-end;
      flex-grow: 1;
    }

    &__clear-button {
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
      font-weight: var(--ds-font-weight-semibold);
      color: var(--ds-color-text-interactive);
      cursor: pointer;
      padding: 0 var(--ds-spacing-inline-sm);

      &:hover {
        color: var(--ds-color-text-interactive);
      }
    }

    &__filter-button {
      padding-right: var(--ds-spacing-inline-sm);
    }

    &__modal {
      z-index: var(--zindex-overlay);
      position: fixed;
      top: 0;
      left: 0;
    }
  }

  ::v-deep {
    .ds-button--icon {
      height: 32px;
      width: 32px;
    }

    .ds-icon {
      height: 16px;
      width: 16px;
    }
  }
</style>
